import React, { useEffect, useRef, useState } from "react";
import { Activity } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/lang";

// Animates toward the real value over ~700ms (ease-out) instead of snapping
// straight to it, so the counter visibly ticks up/down when today's usage
// changes (e.g. right after starting a session) rather than jumping.
function useAnimatedNumber(target, duration = 700) {
    const [display, setDisplay] = useState(target);
    const fromRef = useRef(target);

    useEffect(() => {
        const from = fromRef.current;
        const to = target;
        if (from === to) return;
        let raf;
        const start = performance.now();
        const tick = (now) => {
            const progress = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setDisplay(Math.round(from + (to - from) * eased));
            if (progress < 1) {
                raf = requestAnimationFrame(tick);
            } else {
                fromRef.current = to;
            }
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [target, duration]);

    return display;
}

export default function DailyUsageBadge() {
    const { dailyUsage } = useAuth();
    const { t } = useLang();
    const animatedUsed = useAnimatedNumber(dailyUsage.used);

    if (dailyUsage.limit == null) return null;

    return (
        <div
            className="flex items-center gap-2 px-3 py-1.5 border border-slate-300 text-sm"
            data-testid="nav-daily-usage"
            title={t("nav.daily_uses_hint")}
        >
            <Activity size={14} className="text-slate-500" />
            <span className="font-mono text-slate-900" data-testid="nav-daily-usage-count">
                {animatedUsed}/{dailyUsage.limit}
            </span>
            <span className="mono-label">{t("nav.daily_uses")}</span>
        </div>
    );
}
